/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import {
  FileUploaderDropContainer,
  FileUploaderItem,
  InlineNotification,
  Modal
} from 'carbon-components-react';
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAppDispatch, useAppSelector } from '../../../../../store';
import { LoaderStates } from '../../../../../ts';
import { selectEnvironmentIdFromFilters } from '../../../../analytics/store/selectors';
import { addMessage } from '../../../../ia/store/messages/actions';
import { getAssistantId } from '../../../../ia/store/session/selectors';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import { importCatalogItem } from '../../../store/catalog/actions';
import {
  selectCatalogItemById,
  selectDraftCatalogErrors
} from '../../../store/catalog/selectors';
import { updateIntegration } from '../../../store/channels/actions';
import {
  selectDraftIntegration,
  selectIsIntegrationDirty
} from '../../../store/channels/selectors';
import * as openAPISpecParser from '../../../util/openAPISpecParser';
import { sharedMessages } from '../../messages';
import { AUTHENTICATION_METHODS } from '../constants';
import {
  getAuthenticationMethods,
  onFileAdded,
  onFileRemoved
} from '../helpers';
import { ImportErrors } from '../register/ImportErrors';
import { Register as RegisterMessages } from '../register/messages';
import { FIELDS } from '../schema';
import { MappingStepMessages as messages } from './messages';

import '../register/CatalogOpenApiOverlay.scss';

interface UpdateCatalogOpenApiSpecModalProps {
  catalogItemId: string;
  isModalOpen?: boolean;
  setIsModalOpen?: (boolean) => void;
  setUpdatingCatalogItemSpecState?: (boolean) => void;
}

export const UpdateCatalogOpenApiSpecModal: React.FC<
  UpdateCatalogOpenApiSpecModalProps
> = ({
  catalogItemId,
  isModalOpen,
  setIsModalOpen,
  setUpdatingCatalogItemSpecState
}) => {
  const dispatch = useAppDispatch();
  const { formatMessage: t } = useIntl();

  const formMethods = useFormContext();
  const { getFieldValue, updateFieldValue } = useFormHelpers(formMethods);

  const assistantId = useAppSelector(getAssistantId);
  const environmentId = useAppSelector(selectEnvironmentIdFromFilters);
  const draftIntegration = useAppSelector(selectDraftIntegration);
  const { importErrors, importErrorSubject } = useAppSelector(
    selectDraftCatalogErrors
  );
  const isDirty = useAppSelector(selectIsIntegrationDirty);

  const catalogItem = useAppSelector(state =>
    selectCatalogItemById(state, catalogItemId)
  );
  const [importFileName, setImportFileName] = useState<string>('');
  const [importing, setImporting] = useState<boolean>(false);
  const [updatedSpecBody, setUpdatedSpecBody] = useState(undefined);
  const [isSpecValid, setIsSpecValid] = useState<boolean>(false);
  const [authRemoved, setAuthRemoved] = useState<boolean>(false);
  const [serverUrlRemoved, setServerUrlRemoved] = useState<boolean>(false);
  const [readyForIntegrationUpdate, setReadyForIntegrationUpdate] =
    useState<boolean>(false);

  const handleFileAdd = useCallback(
    (event, { addedFiles }) =>
      onFileAdded({
        dispatch,
        addedFiles,
        setImporting,
        setImportFileName,
        transformOpenAPISpec: openAPISpecParser.transformOpenAPISpec,
        setUpdatedSpecBody,
        updateFieldValue: () => {}
      }),
    [dispatch]
  );

  const handleFileRemoved = useCallback(() => {
    onFileRemoved({
      setImportFileName,
      dispatch,
      setUpdatedSpecBody,
      updateFieldValue
    });
    setAuthRemoved(false);
    setServerUrlRemoved(false);
  }, [dispatch, updateFieldValue]);

  const handleCloseModal = useCallback(() => {
    setImportFileName('');
    setIsModalOpen(false);
    setIsSpecValid(false);
    setAuthRemoved(false);
    setServerUrlRemoved(false);
    setUpdatedSpecBody(undefined);
  }, [setIsModalOpen]);

  /**
   * Wrapper method to set the server URL and its corresponding
   * server variables as both the plain server URL and the generated
   * server URL.
   */
  const updateServerAndVariableFields = useCallback(
    (serverUrl: string) => {
      updateFieldValue(FIELDS.SERVER_URL, serverUrl);
      // Setting new server variables
      const servers = _.cloneDeep(updatedSpecBody?.servers);
      const server = servers?.find(obj => obj.url === serverUrl) || {
        variables: {}
      };
      const newServerVariables = server?.variables || {};
      updateFieldValue(FIELDS.SERVER_VARIABLES, newServerVariables);

      // Variable definitions of the current server definition.
      let generatedServerUrl = _.clone(serverUrl);
      if (!_.isNil(newServerVariables)) {
        Object.keys(newServerVariables).map((key: string) => {
          const val = newServerVariables[key];
          // only set the default value if the user hasn't set one yet
          const value = val.value ?? val.default ?? null;
          updateFieldValue(FIELDS.SERVER_VARIABLE_VALUE(key), value);
          const serverVariableType = getFieldValue(
            FIELDS.SERVER_VARIABLE_TYPE(key)
          );
          if (!serverVariableType || serverVariableType === '') {
            updateFieldValue(FIELDS.SERVER_VARIABLE_TYPE(key), 'value');
          }
          // set the generated server url with variable defaults
          generatedServerUrl = generatedServerUrl.replace(`{${key}}`, value);
        });
      }
      updateFieldValue(FIELDS.GENERATED_SERVER_URL, generatedServerUrl);
    },
    [getFieldValue, updateFieldValue, updatedSpecBody]
  );

  useEffect(() => {
    setIsSpecValid(
      updatedSpecBody && !importing && _.isEmpty(importErrorSubject)
    );
  }, [importing, importErrorSubject, updatedSpecBody]);

  useEffect(() => {
    if (updatedSpecBody && !authRemoved && !serverUrlRemoved) {
      // If the configured auth is no longer an option in the updated spec, show an
      // error to alert Tanya that she needs to update her auth
      const configuredAuthType = getFieldValue(FIELDS.AUTHENTICATION_METHOD);
      const authenticationMethods =
        getAuthenticationMethods(
          updatedSpecBody?.components?.securitySchemes || {}
        ) || [];
      setAuthRemoved(!authenticationMethods.includes(configuredAuthType));
      // If the configured server url is no longer an option in the updated spec, show an
      // error to alert Tanya that she needs to update her auth
      const configuredServerUrl = getFieldValue(FIELDS.SERVER_URL);
      const servers = updatedSpecBody?.servers || [];
      setServerUrlRemoved(
        _.isNil(servers.find(server => server.url === configuredServerUrl))
      );
    }
  }, [
    authRemoved,
    catalogItem,
    getFieldValue,
    serverUrlRemoved,
    updatedSpecBody
  ]);

  useEffect(() => {
    // This useEffect will happen after the handleUpdateOpenAPISpec function above
    // If either auth or server was removed, we must update and save the
    // integration as the current draft integration state
    if (readyForIntegrationUpdate && isDirty) {
      dispatch(
        updateIntegration({
          assistantId,
          environmentId,
          integrationId: draftIntegration.integration_id,
          integration: draftIntegration
        })
      );
      // default values in case Tanya re-updates OpenAPI spec again
      setReadyForIntegrationUpdate(false);
    }
  }, [
    assistantId,
    dispatch,
    draftIntegration,
    environmentId,
    isDirty,
    readyForIntegrationUpdate
  ]);

  /**
   * This function updates the catalog item's config
   */
  const handleUpdateOpenAPISpec = useCallback(async () => {
    setUpdatingCatalogItemSpecState(LoaderStates.ACTIVE);
    // This updates the Catalog Item's specification body with the
    // that of the newly updated specification
    const response = dispatch(
      importCatalogItem({ assistantId, catalogItem, updatedSpecBody })
    );
    response.then(
      () => {
        // Promise fulfilled - Catalog Item spec successfully re-imported
        setUpdatingCatalogItemSpecState(LoaderStates.SUCCESS);
        dispatch(
          addMessage({
            status: 'success',
            title: t(RegisterMessages.uploadSuccessTitle),
            message: t(RegisterMessages.uploadSuccessSubtitle)
          })
        );
      },
      () => {
        // Promise rejected - Catalog Item spec failed to re-import
        setUpdatingCatalogItemSpecState(LoaderStates.ERROR);
      }
    );

    // If the authentication that has been previously set up with this extension is removed, we want
    // to provide an update to Tanya that the authentication has been removed and set to None.
    if (authRemoved) {
      updateFieldValue(
        FIELDS.AUTHENTICATION_METHOD,
        AUTHENTICATION_METHODS.NONE
      );
      dispatch(
        addMessage({
          status: 'error',
          title: t(messages.authenticationWarningTitle),
          message: t(messages.authenticationNotice)
        })
      );
    }
    // If the server url that has been previously set up with this extension is removed, we want
    // to provide an update to Tanya that the authentication has been removed and set to None.
    if (serverUrlRemoved) {
      await updateServerAndVariableFields(updatedSpecBody?.servers[0]?.url);
      dispatch(
        addMessage({
          status: 'error',
          title: t(messages.serverWarningTitle),
          message: t(messages.serverNotice)
        })
      );
    }
    if (authRemoved || serverUrlRemoved) {
      setReadyForIntegrationUpdate(true);
    }
    handleCloseModal();
  }, [
    assistantId,
    authRemoved,
    catalogItem,
    dispatch,
    handleCloseModal,
    serverUrlRemoved,
    setUpdatingCatalogItemSpecState,
    t,
    updateFieldValue,
    updateServerAndVariableFields,
    updatedSpecBody
  ]);

  return (
    <Modal
      className="PhoneModal__addPhoneNumber"
      modalHeading={t(messages.permanentlyReplaceOpenAPIspec)}
      open={isModalOpen}
      primaryButtonText={t(messages.replace)}
      primaryButtonDisabled={
        !Boolean(isSpecValid) || _.isEmpty(updatedSpecBody?.servers)
      }
      secondaryButtonText={t(sharedMessages.cancel)}
      onRequestSubmit={handleUpdateOpenAPISpec}
      onRequestClose={handleCloseModal}
      onSecondarySubmit={handleCloseModal}
      danger>
      <div className="TabContent__subtitle">
        <FormattedMessage {...RegisterMessages.updateImportLabelDescription} />
      </div>
      <div className="TabContent__subtitle">
        <FormattedMessage {...RegisterMessages.updateNote} />
      </div>
      {authRemoved && (
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          title={t(messages.authenticationWarningTitle)}
          subtitle={t(messages.authenticationWarningSubtitle)}
        />
      )}
      {updatedSpecBody && _.isEmpty(updatedSpecBody?.servers) && (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title={t(messages.serversEmptyTitle)}
          subtitle={t(messages.serversEmptySubtitle)}
        />
      )}
      {serverUrlRemoved && !_.isEmpty(updatedSpecBody?.servers) && (
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          title={t(messages.serverWarningTitle)}
          subtitle={t(messages.serverWarningSubtitle)}
        />
      )}
      <div className="CatalogOpenApiFileImport">
        <FileUploaderDropContainer
          accept={['.json']}
          labelText={t(RegisterMessages.importLabelText)}
          multiple={false}
          onAddFiles={handleFileAdd}
        />
        {importFileName && (
          <div className="CatalogOpenApiFileImport__file-item">
            <FileUploaderItem
              name={importFileName}
              status={importing ? 'uploading' : 'edit'}
              onDelete={handleFileRemoved}
              iconDescription={t(RegisterMessages.importOpenApiDelete)}
              invalid={importErrorSubject !== ''}
              errorSubject={importErrorSubject}
              // FileUploaderItem Carbon component does not support react component in errorBody and only accept string
              // This will mostl likely be fixed in carbon 11, until then ignore the console warning on custom extension openAPI spec upload page
              errorBody={importErrors ? <ImportErrors /> : undefined}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};
